import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import Spinner from "../Spinner";
import { useApi } from "../../api/ApiProvider";
import Api from "../../axiosApi/api";
import { useToastMessageQueue } from "../../components/ToastMessages/ToastMessageProvider";
import intl from "components/i18n/ReactIntlWrapper";
import {
  Typography,
  Box,
  Button,
  Chip,
  Tooltip,
  Card
} from "@mui/material";
import { handleAPIError } from 'common/errorHandler';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import { DEFAULT_PAGINATION, useSearch } from 'hooks/useSearch';
import { useDialog } from 'common/DialogProvider';
import SearchInput from "components/SearchInput";
import { SCOPES } from "common/permissions";
import DefaultActions from "components/DataGrid/DefaultActions";
import { getUserEntity } from "common/UserEntityProvider";
import { getTextColor } from "theme/styles/utils";
  
interface TimeOffTypeDTO {
  id?: number;
  idTimeOffRegime?: number;
  timeOffRegime?: any;
  idValidityPeriodType?: number;
  validityPeriodType?: any;
  name?: string | null;
  defaultDays?: number;
  allowNegativeBalance?: boolean;
  maxDaysPerYear?: number;
  maxConsecutiveDays?: number | null;
  requiresSpecialApproval?: boolean;
  validFrom?: string;
  validUntil?: string;
  availableDays?: number;
  isYearly?: boolean;
  isActive?: boolean;
  labelKey?: string | null;
  isDeleted?: boolean;
}

export type RegimeTypesInput = {
  searchTerm?: string | null;
  currentPage?: number;
  sortValue?: string | null;
  orderValue?: string | null;
};

export type SearchResult = {
  totalRecords: number | null;
  queryResult: TimeOffTypeDTO[] | null;
};

const RegimeTypes = () => {
  const { regimeId } = useParams<{ regimeId: string }>();
  const navigate = useNavigate();

  const [input, setInput] = useState<RegimeTypesInput>({
    searchTerm: "",
    currentPage: 1,
    sortValue: "name",
    orderValue: "asc",
  });

  const [regimeName, setRegimeName] = useState<string>("");
  const api: Api = useApi();
  const toast = useToastMessageQueue();
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const entityType = getUserEntity();

  const handleClearSearch = () => {
    setSearchTerm("");
    setInput({ ...input, searchTerm: "" });
  };

  const INITIAL_STATE: GridInitialState = {
    pagination: {
      paginationModel: {
        page: DEFAULT_PAGINATION.pageNum,
        pageSize: DEFAULT_PAGINATION.pageSize,
      },
    },
    sorting: {
      sortModel: [
        {
          field: 'name',
          sort: 'asc',
        },
      ],
    },
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: intl.get('timeOff.table.thead.name'),
      width: 200,
      flex: 1,
    },
    {
      field: 'defaultDays',
      headerName: intl.get('timeOff.table.thead.defaultDays'),
      width: 120,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'expiration',
      headerName: intl.get('timeOff.newRegimeType.form.validity.title'),
      width: 220,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const { idValidityPeriodType, isYearly, maxDaysPerYear, availableDays, validFrom, validUntil, color } = params.row;

        const formatDateRange = (startDate: string | Date, endDate: string | Date) => {
          const start = new Date(startDate);
          const end = new Date(endDate);

          const startFormatted = start.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit'
          });
          const endFormatted = end.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit'
          });
          return `${startFormatted} - ${endFormatted}`;
        };

        const formatQuantityDate = (startDate: string | Date, days: number) => {
          const start = new Date(startDate);
          const startFormatted = start.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
          return `${days} days from ${startFormatted}`;
        };


        if (idValidityPeriodType === 1 || isYearly === true) {
          return (
            <Tooltip title={intl.get('timeOff.newRegimeType.form.validity.year')} placement="top">
              <Chip
                label={maxDaysPerYear ? `${maxDaysPerYear} days/year` : intl.get('timeOff.newRegimeType.form.validity.year')}
                variant="outlined"
                size="small"
                sx={{
                  minWidth: '100px',
                  fontSize: '0.75rem',
                  borderColor: color,
                  borderWidth: '2px',
                  color: getTextColor(color),
                  backgroundColor: `${color}33`
                }}
              />
            </Tooltip>
          );
        }
        if (idValidityPeriodType === 2) {
          const hasValidData = availableDays && validFrom;
          const displayText = hasValidData
            ? formatQuantityDate(validFrom, availableDays)
            : availableDays
            ? `${availableDays} days`
            : "-";

          return (
            <Tooltip
              title={hasValidData ? intl.get('timeOff.newRegimeType.form.validity.quantity') : ""} 
              placement="top"
            >
              <Chip
                label={displayText}
                variant="outlined"
                size="small"
                sx={{
                  minWidth: '120px',
                  fontSize: '0.75rem',
                  maxWidth: '180px',
                  borderColor: color,
                  borderWidth: '2px',
                  color: getTextColor(color),
                  backgroundColor: `${color}33`,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              />
            </Tooltip>
          );
        }

        if (idValidityPeriodType === 3) {
          const hasValidRange = validFrom && validUntil;
          const displayText = hasValidRange ? formatDateRange(validFrom, validUntil) : "-";

          return (
            <Tooltip
              title={hasValidRange ? intl.get('timeOff.newRegimeType.form.validity.period') : ""} 
              placement="top"
            >
              <Chip
                label={displayText}
                variant="outlined"
                size="small"
                sx={{
                  minWidth: '100px',
                  fontSize: '0.75rem',
                  borderColor: color,
                  borderWidth: '2px',
                  color: getTextColor(color),
                  backgroundColor: `${color}33`
                }}
              />
            </Tooltip>
          );
        }

        if (maxDaysPerYear) {
          return (
            <Chip 
              label={`${maxDaysPerYear} days/year`}
              variant="outlined"
              size="small"
              sx={{ 
                fontSize: '0.75rem',
                borderColor: color,
                borderWidth: '2px',
                color: getTextColor(color),
                backgroundColor: `${color}33`
              }}
            />
          );
        }

        if (availableDays) {
          const displayText = validFrom
            ? formatQuantityDate(validFrom, availableDays)
            : `${availableDays} days`;

          return (
            <Chip
              label={displayText}
              variant="outlined"
              size="small"
              sx={{
                fontSize: '0.75rem',
                maxWidth: '180px',
                borderColor: color,
                borderWidth: '2px',
                color: getTextColor(color),
                backgroundColor: `${color}33`,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              }}
            />
          );
        }

        if (validFrom && validUntil) {
          return (
            <Chip
              label={formatDateRange(validFrom, validUntil)}
              variant="outlined"
              size="small"
              sx={{ 
                fontSize: '0.75rem',
                borderColor: color,
                borderWidth: '2px',
                color: getTextColor(color),
                backgroundColor: `${color}33`
              }}
            />
          );
        }

        return (
          <Chip
            label="-"
            variant="outlined"
            size="small"
            sx={{ fontSize: '0.75rem' }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: intl.get('timeOff.table.thead.actions'),
      width: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={entityType.entityType}
          onEdit={() => handleEdit(params.row.id)}
          editScopes={[SCOPES['timeoff.rrhh']]}
          onDelete={() => handleDelete(params.row.id)}
          deleteScopes={[SCOPES['timeoff.rrhh']]}
        />
      ),
    },
  ];

  const apiPromise = (
    searchTerm: string,
    pageNum: number,
    pageSize: number,
    sort: string,
    order: string
  ) => {
    return api.timeoffApi.apiVversionTimeOffTypeSearchGet("1", parseInt(regimeId), searchTerm, pageNum, pageSize, sort, order);
  };

  const processError = (error: any) => {
    handleAPIError(error, toast, errors);
    setErrors({ ...errors });
  };

  const { data, loading, onPaginationModelChange, handleSortModelChange, reFetchData, paginationModel } = useSearch<TimeOffTypeDTO>({
    apiPromise: apiPromise,
    searchTerm: searchTerm,
    defaultSort: { sort: 'name', order: 'asc' },
    onError: processError,
    pageKey: 'timeOffRegimeTypes',
  });

  const fetchRegimeName = async () => {
    if (!regimeId) return;
    try {
      const response = await api.timeoffApi.apiVversionTimeOffRegimeIdGet(
        parseInt(regimeId),
        "1"
      );
      if (response.status === 200 && response.data?.data) {
        setRegimeName(response.data.data.name || `Regime ${regimeId}`);
      }
    } catch (error) {
      console.error("Error fetching regime details:", error);
      setRegimeName(`Regime ${regimeId}`);
    }
  };

  useEffect(() => {
    fetchRegimeName();
  }, [regimeId]);

  const { openDialog } = useDialog();

  const handleEdit = (id: number) => {
    navigate(`/regime/${regimeId}/types/edit/${id}`);
  };

  const handleDelete = (id: number) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: `Are you sure you want to delete ${data?.queryResult?.find(r => r.id === id)?.name}?`,
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get("delete.modal.delete.button"),
        handleConfirm: async (message) => {
          try {
            const response = await api.timeoffApi.apiVversionTimeOffTypeIdDelete(
              id,
              "1"
            );
            if (response.status === 200 || response.status === 204) {
              toast.success({
                message: intl.get("timeOff.toast.success.delete")
              });
              reFetchData();
              message.handleClose(message);
            } else {
              throw new Error('Failed to delete time off type');
            }
          } catch (error) {
            console.error('Error deleting time off type:', error);
            toast.error({
              header: intl.get("timeOff.toast.error.delete"),
              body: error?.response?.data?.message || error.message || 'Unknown error occurred'
            });
            message.handleClose(message);
          }
        }
      },
      cancelButton: {
        cancelLabel: intl.get("delete.modal.cancel.button"),
        handleCancel: (message) => {
          message.handleClose(message);
        },
      },
    });
  };

  const handleBackToRegimes = () => {
    navigate('/regime');
  };

  return (
    <Card sx={{ m: 2 }}>
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToRegimes}
          sx={{ mr: 2 }}
        >
          {intl.get("common.back")}
        </Button>
      </Box>
      <Typography color="primary" variant="h2" component="h2">
          {regimeName}
        </Typography>
      <Box sx={{ p: 3 }}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
          <SearchInput value={searchTerm} onChange={setSearchTerm} onClear={handleClearSearch} />
          <Button onClick={() => navigate(`/regime/${regimeId}/types/new`)}
          >
            <AddIcon /> {intl.get('regimeType.new') ? intl.get('regimeType.new') : intl.get('button.new')}
          </Button>
        </Box>
        <Box style={{ width: '100%', display: 'flex' }}>
          {loading && <Spinner />}
          {(data?.queryResult || (!loading && data?.queryResult === null)) && (
            <DataGrid
              rows={data?.queryResult || []}
              columns={columns}
              rowCount={data?.totalRecords || 0}
              loading={loading}
              paginationModel={paginationModel}
              onPaginationModelChange={onPaginationModelChange}
              onSortModelChange={handleSortModelChange}
            />
          )}
        </Box>
      </Box>
    </Box>
    </Card>
  );
};

export default RegimeTypes;
